/**
 * Comparateur à poignée (élément <compare-slider>), partagé par la section compare-slider et par la section multitile
 * quand elle compare ses deux premières tuiles (syde/wyomind#83). Son curseur (input range, de 0 à 100) donne la part
 * de la largeur laissée au côté de début, posée en --compare-position sur l'élément : le CSS de la section en tire la
 * découpe des deux côtés et la place de la poignée. Optionnel, dans l'élément :
 * - [data-compare-side="start" | "end"] : un côté. Il est inerte (hors tabulation, hors clic) tant que son
 *   [data-compare-guard], son bouton, n'est pas entièrement découvert ; ses [data-compare-reveal] reçoivent la classe
 *   qu'ils nomment quand le quart de leur étendue est découvert, le comparateur arrivé à l'écran, et la perdent quand
 *   ils sont entièrement masqués : un tracé s'efface et se retrace à la découverte suivante.
 * - [data-compare-label="start" | "end"] : l'étiquette d'un côté, masquée (is-hidden) quand elle chevaucherait le
 *   bouton de son côté ou sortirait du comparateur ; les deux étiquettes composent aussi la valeur lue au lecteur
 *   d'écran.
 * - data-key-step sur l'élément : le pas des flèches, en points ; Page précédente et Page suivante avancent de 25,
 *   Début et Fin mènent aux bords. Sans lui, le clavier garde le comportement natif du curseur.
 * Ces règles ne jouent que lorsque le curseur est affiché : masqué (la section multitile sous 750 px), les côtés
 * redeviennent ordinaires.
 */
if (!customElements.get('compare-slider')) {
  customElements.define(
    'compare-slider',
    class CompareSlider extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('input[type="range"]');
        if (!this.range) return;

        this.update = this.update.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.range.addEventListener('input', this.update);
        if (this.dataset.keyStep) this.range.addEventListener('keydown', this.onKeydown);

        // La largeur change la géométrie des côtés : bouton découvert, étiquettes, tracés
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this);
        // Un tracé attend que le comparateur arrive à l'écran, comme les traits hors comparateur
        this.screenObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.onScreen = true;
          this.screenObserver.disconnect();
          this.update();
        });
        this.screenObserver.observe(this);
        this.update();
      }

      disconnectedCallback() {
        if (!this.range) return;
        this.range.removeEventListener('input', this.update);
        this.range.removeEventListener('keydown', this.onKeydown);
        this.resizeObserver.disconnect();
        this.screenObserver.disconnect();
      }

      /** Position de la poignée, en pourcentage de la largeur depuis le bord de début */
      get position() {
        return Math.min(100, Math.max(0, parseFloat(this.range.value) || 0));
      }

      onKeydown(event) {
        const step = parseFloat(this.dataset.keyStep);
        const steps = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step, PageDown: -25, PageUp: 25 };
        let next = null;

        if (event.key in steps) next = this.position + steps[event.key];
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = 100;
        if (next === null) return;

        event.preventDefault();
        this.range.value = Math.min(100, Math.max(0, next));
        this.update();
      }

      update() {
        const position = this.position;
        this.style.setProperty('--compare-position', `${position}%`);

        const sides = this.querySelectorAll('[data-compare-side]');
        const labels = this.querySelectorAll('[data-compare-label]');
        this.updateValueText(labels, position);

        if (!this.range.getClientRects().length) {
          sides.forEach((side) => (side.inert = false));
          return;
        }

        // Abscisse de la poignée à l'écran ; le côté de début est à gauche, sauf en écriture de droite à gauche
        const box = this.getBoundingClientRect();
        const rtl = getComputedStyle(this).direction === 'rtl';
        const handle = box.left + (box.width * (rtl ? 100 - position : position)) / 100;
        const onLeft = (name) => (name === 'start') !== rtl;

        sides.forEach((side) => {
          const left = onLeft(side.dataset.compareSide);
          const guard = side.querySelector('[data-compare-guard]');
          if (guard) {
            const rect = guard.getBoundingClientRect();
            side.inert = left ? rect.right > handle : rect.left < handle;
          }

          side.querySelectorAll('[data-compare-reveal]').forEach((element) => {
            const className = element.dataset.compareReveal;
            const extent = this.extent(element);
            if (!extent) return;
            const shown = left ? handle - extent.left : extent.right - handle;
            if (shown <= 0) element.classList.remove(className);
            else if (this.onScreen && shown >= (extent.right - extent.left) / 4) element.classList.add(className);
          });
        });

        labels.forEach((label) => {
          const side = this.querySelector(`[data-compare-side="${label.dataset.compareLabel}"]`);
          const guard = side && side.querySelector('[data-compare-guard]');
          const rect = label.getBoundingClientRect();
          let hidden = rect.left < box.left || rect.right > box.right;
          if (guard && !hidden) {
            const zone = guard.getBoundingClientRect();
            const margin = 8;
            hidden =
              rect.left < zone.right + margin &&
              rect.right > zone.left - margin &&
              rect.top < zone.bottom + margin &&
              rect.bottom > zone.top - margin;
          }
          label.classList.toggle('is-hidden', hidden);
        });
      }

      /** Valeur lue au lecteur d'écran : la part de chaque côté, nommé par son étiquette */
      updateValueText(labels, position) {
        const names = {};
        labels.forEach((label) => (names[label.dataset.compareLabel] = label.textContent.trim()));
        if (!names.start || !names.end) return;
        const share = Math.round(position);
        this.range.setAttribute('aria-valuetext', `${names.start} ${share}%, ${names.end} ${100 - share}%`);
      }

      /** Étendue horizontale d'un élément et de ses descendants affichés : un tracé peut déborder de son conteneur */
      extent(element) {
        let left = Infinity;
        let right = -Infinity;
        [element, ...element.querySelectorAll('*')].forEach((node) => {
          const rect = node.getBoundingClientRect();
          if (!rect.width) return;
          left = Math.min(left, rect.left);
          right = Math.max(right, rect.right);
        });
        return right > left ? { left, right } : null;
      }
    }
  );
}
